import { i18n } from '@boot/i18n';
import { useSharedStore } from '@stores/shared.js';
import pinia from '@stores/index.js';
import { createApp } from 'vue';
import App from '../App.vue';
import { useAuthStore } from '@stores/auth';

/**
 * Use the shared Pinia instance from stores/index.js
 * This ensures consistent state management across the application
 * For more information see:
 * https://pinia.vuejs.org/core-concepts/outside-component-usage.html
 */
const app = createApp(App);
app.use(pinia);

export default ({ router, store }) => {
    async function loadFacilitiesIfNeeded(to, next) {
        // before we load facilities, we check if we are not being redirected from a loading facilities error
        // so that we don't enter an infinite loop
        if (!(to.name === 'facility_list' && to.query.showReloadBanner)) {
            const facilities = store.getters['facilities/getFacilities'];

            // if facilities is not null, then an attempt to load facilities was already previously made
            if (facilities === null) {
                await store.dispatch('facilities/loadFacilities').catch(() => {
                    next({
                        name: 'facility_list',
                        query: {
                            showReloadBanner: true
                        }
                    });
                });
            }
        }
    }

    // log router errors
    router.onError(error => {
        console.error('Router error: ');
        console.error('Name: ', error.name);
        console.error('Type: ', error.type);
        console.error('Message: ', error.message);
        console.error('Request: ', error.request);
    });

    router.beforeEach(async (to, from, next) => {
        const isAuthenticated = await useAuthStore().isAuthenticatedAction();

        // initialize application on refresh
        if (isAuthenticated) {
            await loadFacilitiesIfNeeded(to, next);

            // load assets
            if ('facilityId' in to.params) {
                // select this facility
                const facilityId = to.params['facilityId'];
                store.dispatch('facilities/selectFacility', facilityId);

                // Load assets. Only proceed with making the request to load asset
                // if we are loading assets for this facility for the first time
                // in order to prevent infinite loop.
                const assets =
                    store.getters['facilities/getAssets'](facilityId);
                const checkAssetsLoadingFailed =
                    store.getters['facilities/checkAssetsLoadingFailed'];
                if (
                    assets.length === 0 &&
                    !checkAssetsLoadingFailed(facilityId)
                ) {
                    await store
                        .dispatch('facilities/loadAssets', {
                            facilityId: facilityId
                        })
                        .catch(() => {
                            store.dispatch(
                                'facilities/addLoadingAssetsFailed',
                                facilityId
                            );
                            next({
                                name: 'facility_dashboard',
                                params: {
                                    facilityId: facilityId
                                }
                            });
                        });
                }
            }
        }

        // check permissions
        if (
            to.matched.some(
                // if isPublic not in record.meta, treat it as private
                record => !record.meta.isPublic || !('isPublic' in record.meta)
            )
        ) {
            // if route is private, check if user is authenticated
            if (isAuthenticated) {
                handleRedirect(to, next);
            } else {
                const hasValidRefreshToken =
                    await useAuthStore().hasValidRefreshTokenAction();
                if (!hasValidRefreshToken) {
                    // Set a notification popup to inform the user that their session has expired
                    useSharedStore().setNotification({
                        type: 'error',
                        message: i18n.global.t(
                            'Your session has expired. Please log in again to continue.'
                        )
                    });
                    next({
                        name: 'login'
                    });
                }

                // navigate them to the login page
                next({
                    name: 'login',
                    query: { redirect: to.fullPath }
                });
            }
        } else {
            // if route is login, check if user is already authenticated, if so navigate them to home page
            // to prevent another user from logging in and sharing session states
            if (isAuthenticated && to.name === 'login') {
                handleRedirect(to, next, 'facility_list');
            } else {
                next();
            }
        }
    });
};

/**
 * Handle redirect to the redirect page if it exists
 * @param {Object} to - the route object
 * @param {Function} next - the next function
 * @param {String} defaultPath - the default path to navigate to if redirect does not exist
 */
function handleRedirect(to, next, defaultPath) {
    const { redirect, ...query } = to.query;
    if (redirect) {
        // navigate to the redirect page if it exists
        // and pass on all extra query parameters
        next({ path: redirect, query: query });
    } else {
        // if no redirect page, navigate to the default page if it exists
        if (defaultPath) {
            next({ name: defaultPath, query: query });
        } else {
            next();
        }
    }
}
